/**
 * SsoProvidersPage
 *
 * Admin page to configure which SSO providers appear on the login screen.
 * OAuth credentials are managed in GCP Identity Platform — not here.
 * This page only controls which configured providers are surfaced to users.
 *
 * Route: /settings/sso  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { Trash2, Plus, Info, ShieldCheck } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import Toggle from "../../components/shared/Toggle";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import { getOrgSettings, updateOrgSettings } from "../../api/adminUsers";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes";

// ─── Well-known OAuth providers ───────────────────────────────────────────────
const KNOWN_PROVIDERS = [
  { id: "google.com",    label: "Google",    type: "oauth" },
  { id: "microsoft.com", label: "Microsoft", type: "oauth" },
  { id: "github.com",    label: "GitHub",    type: "oauth" },
  { id: "apple.com",     label: "Apple",     type: "oauth" },
];

const VALID_CUSTOM_TYPES = ["oidc", "saml"];

// ─── Page ─────────────────────────────────────────────────────────────────────
const SsoProvidersPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const orgAvailable = UIStore((state) => state.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Full list of enabled providers as stored in Firestore
  const [providers, setProviders] = useState([]);

  // New custom provider form state
  const [addType, setAddType] = useState("oidc");
  const [addId, setAddId] = useState("");
  const [addLabel, setAddLabel] = useState("");
  const [addError, setAddError] = useState("");

  // Guard: redirect non-admins
  useEffect(() => {
    if (orgAvailable === false) {
      navigate(ROUTES.SETTINGS.MODELS, { replace: true });
    }
  }, [orgAvailable, navigate]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrgSettings();
      setProviders(data.sso_providers ?? []);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load SSO settings.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // ── Known provider helpers ────────────────────────────────────────────────
  const isKnownEnabled = (id) => providers.some((p) => p.id === id);

  const toggleKnown = (provider) => {
    if (isKnownEnabled(provider.id)) {
      setProviders((prev) => prev.filter((p) => p.id !== provider.id));
    } else {
      setProviders((prev) => [...prev, { ...provider }]);
    }
  };

  // ── Custom provider helpers ───────────────────────────────────────────────
  const customProviders = providers.filter((p) => p.type === "oidc" || p.type === "saml");

  const removeCustom = (id) => {
    setProviders((prev) => prev.filter((p) => p.id !== id));
  };

  const handleAddCustom = () => {
    setAddError("");
    const trimId = addId.trim();
    const trimLabel = addLabel.trim();

    if (!trimId || !trimLabel) {
      setAddError("Provider ID and label are required.");
      return;
    }

    const expectedPrefix = `${addType}.`;
    if (!trimId.startsWith(expectedPrefix)) {
      setAddError(`Provider ID must start with "${expectedPrefix}".`);
      return;
    }

    if (providers.some((p) => p.id === trimId)) {
      setAddError("A provider with this ID already exists.");
      return;
    }

    setProviders((prev) => [...prev, { id: trimId, label: trimLabel, type: addType }]);
    setAddId("");
    setAddLabel("");
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrgSettings({ ssoProviders: providers });
      showFeedback("SSO providers updated.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save SSO providers.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="SSO Providers"
      description="Choose which login providers appear on the sign-in screen. OAuth app credentials must be configured in GCP Identity Platform."
      maxWidth="3xl"
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6">

          {/* ── Well-known OAuth providers ───────────────────────────────── */}
          <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl p-5 flex flex-col gap-4">
            <div>
              <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">OAuth Providers</p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                Enable providers whose OAuth app is already registered in GCP Identity Platform.
              </p>
            </div>
            <div className="flex flex-col divide-y divide-edge-gray-100 dark:divide-edge-dialog-border">
              {KNOWN_PROVIDERS.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                  <div>
                    <p className="text-sm font-medium text-ink-ink dark:text-ink-white">{p.label}</p>
                    <p className="text-xs text-ink-gray-400 font-mono">{p.id}</p>
                  </div>
                  <Toggle checked={isKnownEnabled(p.id)} onChange={() => toggleKnown(p)} />
                </div>
              ))}
            </div>
          </div>

          {/* ── Custom OIDC / SAML providers ─────────────────────────────── */}
          <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl p-5 flex flex-col gap-4">
            <div>
              <p className="text-sm font-semibold text-ink-ink dark:text-ink-white">Custom OIDC / SAML Providers</p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                Add OIDC or SAML providers configured in GCP Identity Platform.
                OIDC IDs must start with <code className="font-mono">oidc.</code> and SAML with <code className="font-mono">saml.</code>
              </p>
            </div>

            {/* Existing custom providers list */}
            {customProviders.length > 0 ? (
              <div className="flex flex-col divide-y divide-edge-gray-100 dark:divide-edge-dialog-border">
                {customProviders.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div>
                      <p className="text-sm font-medium text-ink-ink dark:text-ink-white">{p.label}</p>
                      <p className="text-xs text-ink-gray-400 font-mono">{p.id}</p>
                    </div>
                    <button
                      onClick={() => removeCustom(p.id)}
                      className="p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-red-600 hover:bg-fill-red-50 dark:hover:bg-fill-red-600/10 transition-colors"
                      title="Remove provider"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500">No custom providers added.</p>
            )}

            {/* Add new custom provider form */}
            <div className="border-t border-edge-gray-100 dark:border-edge-dialog-border pt-4 flex flex-col gap-3">
              <p className="text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300">Add Provider</p>
              <div className="flex gap-2 flex-wrap">
                <Select
                  value={addType}
                  onChange={setAddType}
                  options={VALID_CUSTOM_TYPES.map((t) => ({ value: t, label: t.toUpperCase() }))}
                  className="w-28"
                />
                <input
                  type="text"
                  placeholder={`${addType}.my-provider`}
                  value={addId}
                  onChange={(e) => setAddId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
                  className="photon-field flex-1 min-w-[160px] px-3 py-2 text-sm rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-deep-surface text-ink-gray-700 dark:text-ink-gray-300 placeholder-ink-gray-400 focus:outline-none focus:ring-2 focus:ring-focus-gradient-primary"
                />
                <input
                  type="text"
                  placeholder="Display label"
                  value={addLabel}
                  onChange={(e) => setAddLabel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
                  className="photon-field flex-1 min-w-[140px] px-3 py-2 text-sm rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-deep-surface text-ink-gray-700 dark:text-ink-gray-300 placeholder-ink-gray-400 focus:outline-none focus:ring-2 focus:ring-focus-gradient-primary"
                />
                <SecondaryButton onClick={handleAddCustom} className="flex items-center gap-1.5 flex-shrink-0">
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </SecondaryButton>
              </div>
              {addError && (
                <p className="text-xs text-ink-red-600 dark:text-ink-red-400">{addError}</p>
              )}
            </div>
          </div>

          {/* ── GCP note ─────────────────────────────────────────────────── */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-fill-blue-50 dark:bg-fill-gradient-primary/10 border border-edge-blue-200 dark:border-edge-gradient-primary/30">
            <Info className="w-4 h-4 text-ink-blue-600 dark:text-ink-blue-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-ink-blue-800 dark:text-ink-blue-300 space-y-1 leading-relaxed">
              <p className="font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                OAuth credentials are managed in GCP
              </p>
              <p>
                Enabling a provider here only shows its button on the login screen.
                The actual OAuth client ID, secret, and redirect URIs must be configured in the{" "}
                <span className="font-semibold">GCP Identity Platform</span> console for your project.
                Enabling a provider that is not registered there will result in sign-in errors.
              </p>
            </div>
          </div>

          {/* ── Save ─────────────────────────────────────────────────────── */}
          <div className="flex justify-end">
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </PrimaryButton>
          </div>

        </div>
      )}
    </SettingsPageShell>
  );
};

export default SsoProvidersPage;
